export type SeekCvParseErrorBody = {
  error_code?: string;
  message?: string;
  request_id?: string;
  details?: Record<string, unknown>;
};

export type SeekCvParseFriendlyError = {
  code: string;
  message: string;
  requestId?: string;
  supportText?: string;
};

const DEFAULT_MESSAGE = 'We could not parse this CV right now. Please retry or continue manually.';

const FRIENDLY_MESSAGES: Record<string, string> = {
  CV_FILE_TYPE_UNSUPPORTED: 'Please upload a PDF, JPG, PNG, or WebP CV.',
  CV_FILE_TOO_LARGE: 'Please upload a CV smaller than 10 MB.',
  CV_TEXT_EXTRACTION_EMPTY: 'We could not read enough text from this CV. Try a clearer PDF or continue manually.',
  FORK_INHOUSE_VISION_OCR_REQUIRED: 'This CV needs OCR processing that is temporarily unavailable. Try a text-based PDF or continue manually.',
  FORKSEEK_PROFILE_BOOTSTRAP_FAILED: 'We could not finish preparing your ForkSeek profile. Please retry or continue manually.',
  FORKSEEK_PROFILE_NOT_FOUND: 'We could not finish preparing your ForkSeek profile. Please retry or continue manually.',
  CV_DOWNLOAD_FAILED: 'We could not read the uploaded CV. Please retry the upload.',
  CV_DOCUMENT_NOT_FOUND: 'We could not find the uploaded CV. Please retry the upload.',
  UNAUTHORIZED: 'Please sign in again before importing your CV.',
  CV_PARSE_RUNTIME_FAILED: DEFAULT_MESSAGE,
  CV_STRUCTURING_MODEL_NOT_CONFIGURED: DEFAULT_MESSAGE,
  CV_STRUCTURING_MODEL_CALL_FAILED: DEFAULT_MESSAGE,
  CV_STRUCTURING_INVALID_JSON: DEFAULT_MESSAGE,
  CV_STRUCTURING_SCHEMA_VALIDATION_FAILED: DEFAULT_MESSAGE,
  CV_STRUCTURING_TIMEOUT: DEFAULT_MESSAGE,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export async function parseSeekCvFunctionError(error: unknown): Promise<SeekCvParseFriendlyError> {
  let body: SeekCvParseErrorBody | null = null;

  if (isRecord(error)) {
    const context = error.context;
    if (context && typeof (context as { json?: unknown }).json === 'function') {
      body = await (context as Response).json().catch(() => null);
    } else if (isRecord(error.details)) {
      body = error.details as SeekCvParseErrorBody;
    }
  }

  const code = typeof body?.error_code === 'string' ? body.error_code : 'CV_PARSE_RUNTIME_FAILED';
  const message =
    FRIENDLY_MESSAGES[code] ||
    (typeof body?.message === 'string' && body.message.trim() ? body.message.trim() : DEFAULT_MESSAGE);
  const requestId = typeof body?.request_id === 'string' ? body.request_id : undefined;

  return {
    code,
    message,
    requestId,
    supportText: requestId ? `Request ID: ${requestId}` : undefined,
  };
}
